import {
	Badge,
	Box,
	Button,
	Icons,
	Inline,
	RelativeTime,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { useXHR } from "@sembark-travel/xhr"
import { useCallback, useState } from "react"
import { JSONPreview } from "../Integrations/JsonPreview"
import { RunTraceDialog } from "./RunTraceDialog"
import { TAutomationRun, TAutomationScheduledRun, XHR } from "./store"

function formatContext(run: TAutomationScheduledRun) {
	if (!run.triggered_by_type || !run.triggered_by_id) {
		return "System"
	}

	return `${run.triggered_by_type} #${run.triggered_by_id}`
}

function toTraceRun(run: TAutomationScheduledRun): TAutomationRun | null {
	if (!run.automation_run) {
		return null
	}

	return {
		id: run.automation_run.id,
		automation_id: run.automation_id,
		automation: run.automation || null,
		source_key:
			run.automation_run.source_key || run.automation?.source_key || "",
		run_mode: run.automation_run.run_mode,
		triggered_by_type: run.triggered_by_type,
		triggered_by_id: run.triggered_by_id,
		status: run.automation_run.status,
		error: run.automation_run.error,
		retry: null,
		current_step_position: null,
		resume_at: null,
		context: null,
		provenance: {
			type: "schedule",
			schedule: {
				automation_scheduled_run_id: run.id,
				checkpoint_key: run.checkpoint_key,
				scheduled_for: run.scheduled_for,
				mode:
					run.mode ||
					(typeof run.payload?.mode === "string" ? run.payload.mode : null),
				field:
					run.field ||
					(typeof run.payload?.field === "string" ? run.payload.field : null),
				occurrence_index:
					typeof run.occurrence_index === "number"
						? run.occurrence_index
						: typeof run.payload?.occurrence_index === "number"
							? run.payload.occurrence_index
							: undefined,
			},
		},
		started_at: run.automation_run.started_at,
		completed_at: run.automation_run.completed_at,
		failed_at: run.automation_run.failed_at,
		cancelled_at: run.automation_run.cancelled_at,
		created_at: run.created_at,
		updated_at: run.updated_at,
	}
}

function statusBadge(status: string, title?: string | null) {
	return (
		<Badge
			success={status === "completed"}
			warning={
				status === "pending" || status === "processing" || status === "running"
			}
			danger={
				status === "failed" || status === "cancelled" || status === "skipped"
			}
			title={title || undefined}
		>
			{status}
		</Badge>
	)
}

export function ScheduleTraceDialog({
	scheduledRun,
}: {
	scheduledRun: TAutomationScheduledRun
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()
	const [detail, setDetail] = useState<TAutomationScheduledRun | null>(null)
	const [loading, setLoading] = useState(false)

	const handleOpen = useCallback(async () => {
		openDialog()
		setLoading(true)
		try {
			const response = await XHR(xhr).showSchedule(scheduledRun.id)
			setDetail(response.data)
		} finally {
			setLoading(false)
		}
	}, [openDialog, scheduledRun.id, xhr])

	const activeRun = detail || scheduledRun
	const traceRun = toTraceRun(activeRun)
	const scheduleMode =
		activeRun.mode ||
		(typeof activeRun.payload?.mode === "string"
			? activeRun.payload.mode
			: null)
	const scheduleField =
		activeRun.field ||
		(typeof activeRun.payload?.field === "string"
			? activeRun.payload.field
			: null)
	const occurrenceIndex =
		typeof activeRun.occurrence_index === "number"
			? activeRun.occurrence_index
			: typeof activeRun.payload?.occurrence_index === "number"
				? activeRun.payload.occurrence_index
				: null

	return (
		<>
			<Button
				size="sm"
				level="tertiary"
				onClick={handleOpen}
				title="View Details"
			>
				<Icons.ArrowExpand />
			</Button>
			<Dialog
				open={isOpen}
				onClose={closeDialog}
				title={`Schedule #${scheduledRun.id} Details`}
				xl
			>
				<Dialog.Body>
					<Stack gap="6">
						<Box borderWidth="1" rounded="md" padding="4">
							<Stack gap="3">
								<Inline alignItems="center" justifyContent="between" gap="3">
									<Stack gap="1">
										<Text fontWeight="semibold">
											{activeRun.automation?.source?.name ||
												activeRun.automation?.source_key ||
												"Automation"}
										</Text>
										<Text color="muted" fontSize="sm">
											{formatContext(activeRun)}
										</Text>
									</Stack>
									{statusBadge(activeRun.status, activeRun.error)}
								</Inline>

								<Stack gap="1">
									<Text fontWeight="semibold">Why This Schedule Existed</Text>
									<Text color="muted" fontSize="sm">
										Checkpoint: {activeRun.checkpoint_key}
									</Text>
									{scheduleMode ? (
										<Text color="muted" fontSize="sm">
											Mode: {scheduleMode}
										</Text>
									) : null}
									{scheduleField ? (
										<Text color="muted" fontSize="sm">
											Field: {scheduleField}
										</Text>
									) : null}
									{typeof occurrenceIndex === "number" ? (
										<Text color="muted" fontSize="sm">
											Occurrence index: {occurrenceIndex}
										</Text>
									) : null}
								</Stack>

								<Inline gap="6" flexWrap="wrap">
									<Stack gap="1">
										<Text color="muted" fontSize="sm">
											Scheduled For
										</Text>
										<RelativeTime timestamp={activeRun.scheduled_for} />
									</Stack>
									<Stack gap="1">
										<Text color="muted" fontSize="sm">
											Processed
										</Text>
										{activeRun.processed_at ? (
											<RelativeTime timestamp={activeRun.processed_at} />
										) : activeRun.failed_at ? (
											<Text color="muted">Failed before completion</Text>
										) : activeRun.cancelled_at ? (
											<Text color="muted">Cancelled</Text>
										) : (
											<Text color="muted">Not processed yet</Text>
										)}
									</Stack>
								</Inline>

								{activeRun.error ? (
									<Box
										bgColor="danger"
										borderColor="danger"
										borderWidth="1"
										rounded="md"
										padding="3"
									>
										<Text color="danger" fontSize="sm">
											{activeRun.error}
										</Text>
									</Box>
								) : null}
							</Stack>
						</Box>

						<Box borderWidth="1" rounded="md" padding="4">
							<Stack gap="3">
								<Text fontWeight="semibold">Schedule Payload</Text>
								{loading && !detail ? (
									<Text color="muted">Loading details…</Text>
								) : (
									<JSONPreview
										data={
											(activeRun.payload as
												| Record<string, unknown>
												| undefined) || undefined
										}
									/>
								)}
							</Stack>
						</Box>

						<Box borderWidth="1" rounded="md" padding="4">
							<Stack gap="3">
								<Text fontWeight="semibold">Linked Run</Text>
								{activeRun.automation_run ? (
									<>
										<Inline gap="2" alignItems="center">
											<Text>Run #{activeRun.automation_run.id}</Text>
											<Badge
												warning={
													activeRun.automation_run.run_mode === "dry_run"
												}
											>
												{activeRun.automation_run.run_mode === "dry_run"
													? "Dry Run"
													: "Live"}
											</Badge>
											{statusBadge(
												activeRun.automation_run.status,
												activeRun.automation_run.error
											)}
										</Inline>
										{traceRun ? <RunTraceDialog run={traceRun} /> : null}
									</>
								) : (
									<Text color="muted">
										No automation run was created for this scheduled occurrence.
									</Text>
								)}
							</Stack>
						</Box>
					</Stack>
				</Dialog.Body>
			</Dialog>
		</>
	)
}
